import { useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { routeBetween } from "@reagent/sim";
import { nodeAt, useGameStore } from "../../app/gameStore";
import { PALETTE } from "../palette";
import { gridToWorld, TILE_SIZE } from "./gridToWorld";
import { useInteractionStore } from "./interactionStore";

/**
 * While a pipe card waits for its second endpoint, this shows where the line
 * would actually run: a ring on the source machine and a dotted route to
 * whatever is under the cursor.
 *
 * The route comes from the engine's own `routeBetween`, not a lookalike — a
 * preview that disagrees with the pipe you get is worse than no preview.
 */
export function PipeRoutePreview() {
  const pendingPipe = useGameStore((s) => s.pendingPipe);
  const pendingMove = useGameStore((s) => s.pendingMove);
  const snapshot = useGameStore((s) => s.snapshot);
  const hoveredTile = useInteractionStore((s) => s.hoveredTile);

  const groupRef = useRef<THREE.Group>(null);

  useFrame(({ clock }) => {
    const group = groupRef.current;
    if (!group) return;
    // Breathe, so the preview reads as pending rather than placed.
    const pulse = 1 + Math.sin(clock.elapsedTime * 5) * 0.12;
    group.scale.setScalar(pulse);
  });

  // A pipe move (spec 0067) re-lays its line between two fresh endpoints, so
  // once its first machine is chosen it previews exactly like a new pipe.
  const fromNodeId =
    pendingPipe?.fromNodeId ?? (pendingMove?.kind === "pipe" ? pendingMove.fromNodeId : null);
  if (fromNodeId === null) return null;

  const source = snapshot.nodes.find((n) => n.id === fromNodeId);
  if (!source) return null;

  const targetNode = hoveredTile ? nodeAt(snapshot, hoveredTile) : null;
  // Only trace a route to a real machine; hovering bare board shows just the
  // anchor, which is the honest answer — that drop would be refused.
  const route = targetNode ? routeBetween(source.pos, targetNode.pos) : [];

  const [sx, , sz] = gridToWorld(source.pos);

  return (
    <group>
      {/* Anchor ring on the source machine. It rings the tile rather than
          sitting on it, and draws with depthTest off — a marker at board
          height vanishes underneath the very machine it is marking. */}
      <group ref={groupRef} position={[sx, 0.06, sz]}>
        <mesh rotation={[-Math.PI / 2, 0, 0]} renderOrder={900}>
          <ringGeometry args={[TILE_SIZE * 0.46, TILE_SIZE * 0.58, 28]} />
          <meshBasicMaterial
            color={PALETTE.fluidSkyBlue}
            transparent
            opacity={0.95}
            side={THREE.DoubleSide}
            depthWrite={false}
            depthTest={false}
          />
        </mesh>
      </group>

      {route.map((pos, i) => {
        const [x, , z] = gridToWorld(pos);
        const isEnd = i === route.length - 1;
        return (
          <mesh
            key={`${pos.x},${pos.y}`}
            position={[x, 0.06, z]}
            rotation={[-Math.PI / 2, 0, 0]}
            renderOrder={901}
          >
            <circleGeometry args={[isEnd ? TILE_SIZE * 0.3 : TILE_SIZE * 0.15, 16]} />
            <meshBasicMaterial
              color={isEnd ? PALETTE.mustard : PALETTE.fluidSkyBlue}
              transparent
              opacity={isEnd ? 0.95 : 0.8}
              depthWrite={false}
              depthTest={false}
            />
          </mesh>
        );
      })}
    </group>
  );
}
